/**
 * How a report's date parameters are grouped into ranges, and what each is called.
 *
 * ONE source of truth for both ends of PRM-517: the editor's Display Date Filter box
 * (views/Editor/.../Filters/DataFilter.js) previews it, and the board view
 * (views/Dashboard/Filters/WidgetGlobalFilters.js) draws the real controls from it.
 * witmeg-reporting-embed-v2 has to do the same pairing on its side - port this file,
 * do not re-derive it, or a report will group differently in the host app.
 *
 * The rule: date-typed global filters pair BY LIST ORDER. Filters 1+2 are the first
 * period, 3+4 the second. An odd one out cannot make a range.
 *
 * Saved on the resource as:
 *   dateFilterDisplay: { viewBy: "merge" | "separate", periods: [{ name }] }
 *
 * `periods` holds only labels - the dates and the parameter names live in
 * customFilters - and it is sparse: a name is written only once someone renames one,
 * so periods[i] is often undefined on a resource that genuinely has that range.
 */

/* Separate is the default, NOT merged. A report that already had a From and a To
   parameter has always shown them as two fields; defaulting to a merged range would
   silently change how every existing report looks the moment this shipped. Merging is
   opt-in - the author picks it in the editor's Display Date Filter box. */
export const DEFAULT_DATE_DISPLAY = { viewBy: "separate", periods: [] };

/** A resource's saved display config, with the defaults filled in. */
export const readDateDisplay = (resource) => ({
  ...DEFAULT_DATE_DISPLAY,
  ...(resource?.dateFilterDisplay || {}),
});

/**
 * Date-typed filters -> the ranges they make, in list order.
 * @returns {{from: object, to: object|null, incomplete: boolean}[]}
 */
export function buildDatePeriods(filters) {
  const dates = (filters || []).filter((f) => f?.dataType === "date");
  const periods = [];

  for (let i = 0; i < dates.length; i += 2) {
    periods.push({
      from: dates[i],
      // missing when the count is odd - the range cannot be completed
      to: dates[i + 1] || null,
      incomplete: !dates[i + 1],
    });
  }

  return periods;
}

/**
 * Whether these periods should be drawn as date RANGES at all.
 *
 * One lone date filter is not a range - it stays an ordinary date field, which is
 * what a report had before any of this existed.
 */
export const hasDateRanges = (periods) =>
  periods.length > 1 || (periods.length === 1 && !periods[0].incomplete);

/**
 * What to call period `i`.
 *
 * Nothing is stored until it is actually renamed, so the default adapts: a lone range
 * is "Select Date Range", several are "Period 1" / "Period 2". A name that HAS been
 * typed wins either way - it was chosen deliberately.
 */
export const periodName = (dateDisplay, i, multiPeriod) =>
  dateDisplay?.periods?.[i]?.name ?? (multiPeriod ? `Period ${i + 1}` : "Select Date Range");

/** The ids of every filter already accounted for by a range, so callers can skip them. */
export const periodFilterIds = (periods) =>
  new Set(periods.flatMap((p) => [p.from?.id, p.to?.id]).filter((id) => id !== undefined && id !== null));
